export enum PractitionerPermissionFlags {
  Admin = "admin",
  AdminProvider = "admin-provider",
  ManagerRole = "manager-role",
  ProviderRole = "provider-role",
  ScribeRole = "scribe-role",
  BillerRole = "biller-role",
  ReaderRole = "reader-role",
  DoulaRole = "doula-role",
}

export const PRACTICE_USER_PERMISSION_FLAGS_MAPPING = {
  [PractitionerPermissionFlags.ManagerRole]: "Manager Role",
  [PractitionerPermissionFlags.ProviderRole]: "Provider Role",
  [PractitionerPermissionFlags.ScribeRole]: "Scribe Role",
  [PractitionerPermissionFlags.BillerRole]: "Biller Role",
  [PractitionerPermissionFlags.ReaderRole]: "Read-Only Role",
  [PractitionerPermissionFlags.DoulaRole]: "Doula Role",
};

export const PRACTITIONER_PERMISSION_FLAGS_MAPPING = {
  [PractitionerPermissionFlags.Admin]: "Admin",
  [PractitionerPermissionFlags.AdminProvider]: "Admin Provider",
  [PractitionerPermissionFlags.ManagerRole]: "Manager Role",
  [PractitionerPermissionFlags.ProviderRole]: "Provider Role",
  [PractitionerPermissionFlags.ScribeRole]: "Scribe Role",
  [PractitionerPermissionFlags.BillerRole]: "Biller Role",
  [PractitionerPermissionFlags.ReaderRole]: "Read-Only Role",
  [PractitionerPermissionFlags.DoulaRole]: "Doula Role",
};

export const INVITE_PROVIDER_PERMISSION_FLAGS_MAPPING = {
  [PractitionerPermissionFlags.AdminProvider]: "Admin Provider",
  [PractitionerPermissionFlags.ManagerRole]: "Manager Role",
  [PractitionerPermissionFlags.ProviderRole]: "Provider Role",
  [PractitionerPermissionFlags.ScribeRole]: "Scribe Role",
  [PractitionerPermissionFlags.BillerRole]: "Biller Role",
  [PractitionerPermissionFlags.ReaderRole]: "Read-Only Role",
  [PractitionerPermissionFlags.DoulaRole]: "Doula Role",
};

export const PRACTITIONER_PERMISSION_FLAGS_NAMES = Object.values(
  PRACTITIONER_PERMISSION_FLAGS_MAPPING,
);

export const ADMIN_PERMISSION_FLAGS = [PractitionerPermissionFlags.Admin];

export const ADMIN_PROVIDER_PERMISSION_FLAGS = [
  PractitionerPermissionFlags.Admin,
  PractitionerPermissionFlags.AdminProvider,
];

export const MANAGER_PERMISSION_FLAGS = [
  PractitionerPermissionFlags.Admin,
  PractitionerPermissionFlags.AdminProvider,
  PractitionerPermissionFlags.ManagerRole,
];

/**
 * Sign, lock and amend an encounter. Includes DoulaRole: a Doula operates like a
 * Provider. Narrower than PROVIDER_SCRIBE_PERMISSION_FLAGS — Scribes chart but
 * do not sign.
 */
export const PROVIDER_PERMISSION_FLAGS = [
  PractitionerPermissionFlags.Admin,
  PractitionerPermissionFlags.AdminProvider,
  PractitionerPermissionFlags.ProviderRole,
  PractitionerPermissionFlags.DoulaRole,
];

export const READER_PERMISSION_FLAGS = [
  PractitionerPermissionFlags.Admin,
  PractitionerPermissionFlags.AdminProvider,
  PractitionerPermissionFlags.ReaderRole,
];

export const BILLER_PERMISSION_FLAGS = [
  PractitionerPermissionFlags.Admin,
  PractitionerPermissionFlags.AdminProvider,
  PractitionerPermissionFlags.BillerRole,
];

export const SCRIBE_PERMISSION_FLAGS = [
  PractitionerPermissionFlags.Admin,
  PractitionerPermissionFlags.AdminProvider,
  PractitionerPermissionFlags.ScribeRole,
];

export const DOULA_PERMISSION_FLAGS = [
  PractitionerPermissionFlags.Admin,
  PractitionerPermissionFlags.AdminProvider,
  PractitionerPermissionFlags.DoulaRole,
];

/**
 * Clinical charting access. Includes DoulaRole: a Doula operates like a Provider.
 *
 * Lab ordering is the one capability a Doula does not have — gate that with
 * hasLabOrderPermission instead.
 */
export const PROVIDER_SCRIBE_PERMISSION_FLAGS = [
  PractitionerPermissionFlags.Admin,
  PractitionerPermissionFlags.AdminProvider,
  PractitionerPermissionFlags.ProviderRole,
  PractitionerPermissionFlags.ScribeRole,
  PractitionerPermissionFlags.DoulaRole,
];

/**
 * Allows viewing a client's lab orders, results, and Lab Flow Sheet. Includes
 * clinical charting roles and Manager.
 *
 * Manager can only view. Use hasLabOrderPermission to check whether to allow
 * ordering, and hasProviderOrScribeRole to check whether to allow result entry.
 */
export const LAB_RESULTS_VIEW_PERMISSION_FLAGS = [
  PractitionerPermissionFlags.Admin,
  PractitionerPermissionFlags.AdminProvider,
  PractitionerPermissionFlags.ManagerRole,
  PractitionerPermissionFlags.ProviderRole,
  PractitionerPermissionFlags.ScribeRole,
  PractitionerPermissionFlags.DoulaRole,
];

/**
 * Edit the time spent on an encounter. Matches the roles that chart encounters; billers, managers
 * and read-only users see the recorded time but don't edit it.
 */
export const ENCOUNTER_TIME_SPENT_EDIT_PERMISSION_FLAGS = [
  PractitionerPermissionFlags.Admin,
  PractitionerPermissionFlags.AdminProvider,
  PractitionerPermissionFlags.ProviderRole,
  PractitionerPermissionFlags.ScribeRole,
  PractitionerPermissionFlags.DoulaRole,
];

export const CHART_EXPORT_PERMISSION_FLAGS = [
  PractitionerPermissionFlags.Admin,
  PractitionerPermissionFlags.AdminProvider,
  PractitionerPermissionFlags.ProviderRole,
  PractitionerPermissionFlags.ScribeRole,
  PractitionerPermissionFlags.DoulaRole,
  PractitionerPermissionFlags.BillerRole,
];

export const DOT_PHRASE_PERMISSION_FLAGS: [
  PractitionerPermissionFlags,
  ...PractitionerPermissionFlags[],
] = [
  PractitionerPermissionFlags.Admin,
  PractitionerPermissionFlags.AdminProvider,
  PractitionerPermissionFlags.ManagerRole,
  PractitionerPermissionFlags.ProviderRole,
  PractitionerPermissionFlags.ScribeRole,
  PractitionerPermissionFlags.ReaderRole,
  PractitionerPermissionFlags.DoulaRole,
];

/**
 * Apply or remove a flag on a client's chart. Mirrors the `Flag` grants in
 * `app/fhir/seed-data/access-policies/`: Biller and Read-Only are readonly on `Flag`, so a write
 * from either is refused by the server.
 */
export const FLAG_WRITE_PERMISSION_FLAGS = [
  PractitionerPermissionFlags.Admin,
  PractitionerPermissionFlags.AdminProvider,
  PractitionerPermissionFlags.ManagerRole,
  PractitionerPermissionFlags.ProviderRole,
  PractitionerPermissionFlags.ScribeRole,
  PractitionerPermissionFlags.DoulaRole,
];

/**
 * Send a form (consent, intake, assessment) to a client from their chart. Mirrors the `Task`
 * grants in `app/fhir/seed-data/access-policies/`: Biller and Read-Only are readonly on `Task`.
 */
export const FORMS_SEND_PERMISSION_FLAGS = [
  PractitionerPermissionFlags.Admin,
  PractitionerPermissionFlags.AdminProvider,
  PractitionerPermissionFlags.ManagerRole,
  PractitionerPermissionFlags.ProviderRole,
  PractitionerPermissionFlags.ScribeRole,
  PractitionerPermissionFlags.DoulaRole,
];

/**
 * Save an encounter and edit its diagnoses. Includes DoulaRole: a Doula operates like a Provider.
 */
export const PROVIDER_BILLER_SCRIBE_PERMISSION_FLAGS = [
  PractitionerPermissionFlags.Admin,
  PractitionerPermissionFlags.AdminProvider,
  PractitionerPermissionFlags.ProviderRole,
  PractitionerPermissionFlags.BillerRole,
  PractitionerPermissionFlags.ScribeRole,
  PractitionerPermissionFlags.DoulaRole,
];

/** Use client checklists and checklist templates. Includes DoulaRole: a Doula operates like a Provider. */
export const PROVIDER_SCRIBE_READER_PERMISSION_FLAGS = [
  PractitionerPermissionFlags.Admin,
  PractitionerPermissionFlags.AdminProvider,
  PractitionerPermissionFlags.ProviderRole,
  PractitionerPermissionFlags.ScribeRole,
  PractitionerPermissionFlags.ReaderRole,
  PractitionerPermissionFlags.DoulaRole,
];

export const BILLER_MANAGER_PERMISSION_FLAGS = [
  PractitionerPermissionFlags.Admin,
  PractitionerPermissionFlags.AdminProvider,
  PractitionerPermissionFlags.BillerRole,
  PractitionerPermissionFlags.ManagerRole,
];

export const ALL_PERMISSION_FLAGS = [
  PractitionerPermissionFlags.Admin,
  PractitionerPermissionFlags.AdminProvider,
  PractitionerPermissionFlags.ManagerRole,
  PractitionerPermissionFlags.ProviderRole,
  PractitionerPermissionFlags.ScribeRole,
  PractitionerPermissionFlags.BillerRole,
  PractitionerPermissionFlags.ReaderRole,
  PractitionerPermissionFlags.DoulaRole,
];

/**
 * Every role that may write. ReaderRole is absent because it only ever reads; Clients are gated
 * separately with allowPatient.
 *
 * The single source of truth for "may write": pass it to PermissionGate, or call hasWriteRole,
 * which tests membership here.
 *
 * Listed explicitly rather than derived from ALL_PERMISSION_FLAGS so a new role grants nothing
 * until someone adds it here on purpose. The practitioner-permissions test compares the two
 * lengths, so adding a role without making that choice fails the suite.
 */
export const WRITE_PERMISSION_FLAGS: PractitionerPermissionFlags[] = [
  PractitionerPermissionFlags.Admin,
  PractitionerPermissionFlags.AdminProvider,
  PractitionerPermissionFlags.ManagerRole,
  PractitionerPermissionFlags.ProviderRole,
  PractitionerPermissionFlags.ScribeRole,
  PractitionerPermissionFlags.BillerRole,
  PractitionerPermissionFlags.DoulaRole,
];
